import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { publicNavLinks } from "@/mocks/publico";

const LOGO_URL =
  "https://static.readdy.ai/image/f0e3ea51be3a6053767e026ea9261140/5c6a1513dcc1f5b9c75447af1cdc9373.png";

interface PublicHeaderProps {
  onNavigate: (href: string) => void;
}

export default function PublicHeader({ onNavigate }: PublicHeaderProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      onNavigate(href);
      setMobileOpen(false);
    },
    [onNavigate]
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-background-200 shadow-sm"
          : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a
            href="#inicio"
            className="flex items-center gap-2 shrink-0"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("#inicio");
            }}
          >
            <img
              src={LOGO_URL}
              alt="FloraIQ"
              className="h-9 md:h-[34px] w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {publicNavLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            <a
              href="#solicitar"
              onClick={(e) => {
                e.preventDefault();
                onNavigate("#participacao");
              }}
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 border border-background-200 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Solicitar Demonstração
            </a>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-5 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              Acessar Plataforma
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md hover:bg-background-100 text-foreground-700 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i
              className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl w-5 h-5 flex items-center justify-center`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200">
          <nav className="flex flex-col px-4 py-3 gap-1">
            {publicNavLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-50 rounded-md transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-background-200">
              <a
                href="#solicitar"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate("#participacao");
                  setMobileOpen(false);
                }}
                className="px-4 py-2.5 text-sm font-medium text-foreground-700 border border-background-200 rounded-md text-center transition-colors whitespace-nowrap hover:bg-background-50 cursor-pointer"
              >
                Solicitar Demonstração
              </a>
              <button
                onClick={() => {
                  navigate("/dashboard");
                  setMobileOpen(false);
                }}
                className="px-4 py-2.5 text-sm font-semibold text-white rounded-md text-center transition-colors whitespace-nowrap bg-primary-500 hover:bg-primary-600 cursor-pointer"
              >
                Acessar Plataforma
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}